import { getName } from '@home/utils/common';
import { getLastNonEmptyIndex } from '@/pages/dashboard/components/Chart/_config/utils';

export const useChartOptions = ({
  selectedSegment,
  colorArr,
  legendData,
  colorMap,
  currentTab,
  isEN,
  dateMap,
  dateNameMap,
  lastData,
  visibleSeries
}: any) => {
  const mergeOptions = (opt: any) => {
    const options = { ...opt };
    
    // 使用配置的颜色数组
    options.color = colorArr;
    
    // 使用生成的 legendData 和正确的颜色
    const currentLegendData = legendData;
    
    let reversedLegend;
    let reversedColors;
    
    if (selectedSegment === 'competitor') {
      // competitor模式：不反转，保持current_sales在第一位
      reversedLegend = [...currentLegendData];
      reversedColors = [...colorArr];
    } else {
      // forecast模式：使用原有的反转逻辑
      reversedLegend = [...currentLegendData]?.reverse();
      reversedColors = [...colorArr]?.reverse();
    }

    // 设置初始的legend选中状态
    const selected: { [key: string]: boolean; } = {};
    if (visibleSeries && visibleSeries.length > 0) {
      reversedLegend.forEach((name: string) => {
        selected[name] = visibleSeries?.includes(name);
      });
    } else {
      reversedLegend.forEach((name: string) => {
        selected[name] = false;
      });
    }

    options.legend = {
      show: true,
      itemWidth: 8,
      itemHeight: 8,
      bottom: 0,
      icon: 'circle',
      itemGap: 18,
      textStyle: {
        fontSize: 12,
        color: '#7C7C7C',
      },
      selectedMode: true,
      selected,
      data: reversedLegend.map((name, index) => ({
        name,
        itemStyle: {
          opacity: 1,
          color: reversedColors[index]
        }
      }))
    };
    
    options.grid.bottom = 40;
    options.grid.left = 10;
    options.animation = false;
    
    const xData = options?.xAxis[0]?.data;

    if (selectedSegment === 'competitor') {
      // competitor模式：调整样式
      options.series = options?.series?.map((seriesItem: any) => {
        const seriesName = seriesItem.name;
        const seriesColor = colorMap[seriesName];
        
        // 判断是否为current_sales
        const isCurrentSales = seriesName === getName(isEN, 
          currentTab?.componentMetrics.find((m: any) => m.metricKey === 'current_sales')?.enName,
          currentTab?.componentMetrics.find((m: any) => m.metricKey === 'current_sales')?.cnName
        );
        const lastIndex = getLastNonEmptyIndex(seriesItem?.data);
    
        const newData = seriesItem?.data?.map((item: any, index: number) => {
          const value = item?.value ?? item;
          const xName = xData?.[index];
          const hasLabel = !!dateMap?.[xName];
          const isEdgePoint = (index === lastIndex || hasLabel) && index !== 0;
    
          return {
            value: index === 0 ? (value || 0) : value,
            symbol: isEdgePoint ? 'emptyCircle' : 'none',
            symbolSize: isEdgePoint ? 5 : 0,
            itemStyle: {
              opacity: isEdgePoint ? 1 : 0
            }
          };
        });
    
        const seriesConfig: any = {
          name: seriesName,
          type: 'line',
          connectNulls: true,
          data: newData,
          itemStyle: { color: seriesColor },
          lineStyle: {
            color: seriesColor,
            width: 2,
            type: isCurrentSales ? 'solid' : [5, 2] // current_sales用实线，其他竞品用虚线
          },
          symbol: 'circle',
          symbolSize: 4,
          smooth: false
        };
    
        if (isCurrentSales) {
          // 为current_sales添加markLine
          seriesConfig.markLine = {
            symbol: 'none',
            emphasis: { disabled: true },
            label: {
              show: true,
              formatter: lastData,
              position: 'end',
              color: seriesColor,
              offset: [0,2]
            },
            lineStyle: {
              color: seriesColor,
              type: 'dashed'
            },
            data: [{ 
              xAxis: xData?.[lastIndex] ?? (xData?.[xData.length - 1] || 0)
            }]
          };
          seriesConfig.z = 10; // 确保current_sales线在最上层
        }
    
        return seriesConfig;
      });
    } else {
      // forecast模式：使用原有复杂逻辑
      options.series = options?.series?.map((seriesItem: any, seriesIndex: number) => {
        const seriesName = seriesItem.name;
        const seriesColor = colorMap[seriesName];
        
        if (seriesIndex === 0) {
          let foundFirst = false;
          const formattedData = seriesItem?.data?.map((item: { value: string; }, index: number) => {
            const value = item?.value ?? item;
            const xName = xData?.[index];
            const hasLabel = !!dateMap?.[xName];

            if (hasLabel && !foundFirst) {
              foundFirst = true;
              return {
                value: index === 0 ? (value || 0) : value,
                symbol: 'none'
              };
            }
            return {
              value,
              symbol: hasLabel ? 'emptyCircle' : 'none',
              symbolSize: hasLabel ? 5 : 0,
              itemStyle: { opacity: hasLabel ? 1 : 0 }
            };
          });
          return {
            name: seriesItem?.name,
            connectNulls: true,
            type: 'line',
            z: 1,
            data: formattedData,
            stack: 'diff',
            emphasis: { disabled: true },
            itemStyle: { color: seriesColor },
            areaStyle: { color: 'transparent' },
            lineStyle: { type: [5, 2], color: seriesColor },
          };
        }
        
        if (seriesIndex === 1) {
          const baseData = options?.series?.[0]?.data || [];
          let foundFirst = false;

          const newData = seriesItem?.data?.map((item: any, index: number) => {
            const val1 = parseFloat(item?.value ?? item) || 0;
            const val0 = parseFloat(baseData?.[index] ?? 0) || 0;
            const isNull = item?.value === "" && (baseData?.[index]?.value === "" || baseData?.[index]?.value === 0);
            const isShowLower = visibleSeries?.includes(options?.series?.[0]?.name);
            const newValue = isNull ? "" : isShowLower ? val1 - val0 :val1;

            const xName = xData?.[index];
            const hasLabel = !!dateMap?.[xName];

            if (hasLabel && !foundFirst) {
              foundFirst = true;
              return {
                value: index === 0 ? (newValue || 0) : newValue,
                symbol: 'none'
              };
            }

            return {
              value: newValue,
              symbol: hasLabel ? 'emptyCircle' : 'none',
              symbolSize: hasLabel ? 5 : 0,
              itemStyle: { opacity: hasLabel ? 1 : 0 }
            };
          });

          // 判断是否显示背景色：只有当两条线都显示时才显示背景色
          const isShowAreaStyle = visibleSeries && visibleSeries.length >= 2 && 
            visibleSeries.includes(options?.series?.[0]?.name) && 
            visibleSeries.includes(seriesItem?.name);

          return {
            name: seriesItem?.name,
            connectNulls: true,
            type: 'line',
            z: 1,
            data: newData,
            stack: 'diff',
            emphasis: { disabled: true },
            itemStyle: { color: seriesColor },
            areaStyle: isShowAreaStyle ? { color: seriesColor ? `${seriesColor}20` : '#F4F0FE' } : { color: 'transparent' },
            lineStyle: { type: [5, 2], color: seriesColor }
          };
        }
        
        if (seriesIndex === 2) {
          const newData = seriesItem?.data;
          const lastIndex = getLastNonEmptyIndex(newData);
          const formattedData = newData?.map((val: string, idx: number) => {
            const isEdgePoint = (idx === lastIndex || dateMap?.[xData?.[idx]]) && idx !== 0;
            return {
              value: idx === 0 ? (val || 0) : val,
              symbol: isEdgePoint ? 'emptyCircle' : 'none',
              symbolSize: isEdgePoint ? 5 : 0,
              itemStyle: { opacity: isEdgePoint ? 1 : 0 }
            };
          });

          return {
            name: seriesItem?.name,
            type: 'line',
            z: 10,
            connectNulls: true,
            data: formattedData,
            itemStyle: { color: seriesColor },
            label: { ...seriesItem?.label, show: false },
            symbolSize: 7,
            lineStyle: { type: 'line', color: seriesColor },
            markLine: {
              symbol: 'none',
              emphasis: { disabled: true },
              label: {
                show: true,
                formatter: lastData,
                position: 'end',
                color: seriesColor,
                offset: [0,2]
              },
              lineStyle: { color: seriesColor, type: 'dashed' },
              data: [{ 
                xAxis: xData?.[lastIndex] ?? (xData?.[xData.length - 1] || 0)
              }]
            }
          };
        }

        return {
          ...seriesItem,
          itemStyle: { ...seriesItem.itemStyle, color: seriesColor },
          lineStyle: { ...seriesItem.lineStyle, color: seriesColor }
        };
      });
    }
    
    options.xAxis[0].axisLabel.interval = 0;
    options.xAxis[0].axisLabel.formatter = (value: any) => getName(isEN, dateMap?.[value], dateNameMap?.[dateMap?.[value]]);
    options.xAxis[0].axisTick = {
      show: true,
      alignWithLabel: true,
      inside: true,
      interval: (index: number) => {
        const value = options?.xAxis?.[0]?.data?.[index];
        return dateMap?.[value];
      }
    };
    
    return options;
  };

  return { mergeOptions };
};
